import {
  isJamiaCapability,
  isJamiaOrHifzUlQuranCapability,
  isOnlineSchoolCapability,
  isStudentReportsCapability,
  isHifzUlQuranCapability,
  teacherHasGlobalStudentsListAccess,
  usesHifzClassScopedStudentLessons,
  isCertificatesCapability,
} from "../../utils/associateCapabilities";
import i18n from "../../i18n";
import { CERTIFICATE_MENU_BY_ROLE } from "./certificateMenuItems";

const createMenuItem = (titleKey, path, icon, extras = {}) => ({
  titleKey,
  path,
  icon,
  ...extras,
});

const BASE_MENU_ITEMS = {
  dashboard: createMenuItem("sidebar.dashboard", "/dashboard", "mdi:view-dashboard", {
    exact: true,
    accent: "dashboard",
  }),
  feeds: createMenuItem("sidebar.feeds", "/feeds", "mdi:rss", { accent: "feeds" }),
  studentReports: createMenuItem(
    "sidebar.studentsReports",
    "/student-reports",
    "mdi:clipboard-list",
    { accent: "reports" },
  ),
  classRoom: createMenuItem("sidebar.classRoom", "/class-room", "mdi:account-group", {
    accent: "classroom",
  }),
  chats: createMenuItem("sidebar.chats", "/class-room", "mdi:message-text", {
    accent: "classroom",
  }),
  allCalls: createMenuItem("sidebar.allCalls", "/class-calls", "mdi:video", {
    accent: "calls",
  }),
  classes: createMenuItem("sidebar.classes", "/classes", "mdi:school", {
    matches: ["/classes", "/subjects"],
    accent: "classes",
  }),
  classPeriods: createMenuItem(
    "sidebar.classPeriods",
    "/class-periods",
    "mdi:clock-time-four-outline",
    { accent: "periods" },
  ),
  lessonBooks: createMenuItem(
    "sidebar.gallery",
    "/lesson-books",
    "mdi:book-open-page-variant",
    { accent: "gallery" },
  ),
  trainingLesson: createMenuItem(
    "sidebar.trainingLesson",
    "/training-lesson",
    "mdi:earth",
    { accent: "training" },
  ),
  students: createMenuItem("sidebar.students", "/students", "mdi:school", {
    accent: "students",
  }),
  teachers: createMenuItem("sidebar.teachers", "/teachers", "mdi:teach", {
    accent: "teachers",
  }),
  guardians: createMenuItem(
    "sidebar.guardians",
    "/guardians",
    "mdi:account-supervisor",
    { accent: "guardians" },
  ),
  personalNotes: createMenuItem(
    "sidebar.personalNotes",
    "/personal-notes",
    "mdi:notebook",
    { accent: "notes" },
  ),
  toDoLists: createMenuItem(
    "sidebar.toDoLists",
    "/to-do-lists",
    "mdi:format-list-checks",
    { accent: "todos" },
  ),
  settings: createMenuItem("sidebar.settings", "/general-setting", "mdi:cog", {
    accent: "settings",
  }),
  reportedFeeds: createMenuItem(
    "sidebar.reportedFeeds",
    "/reported-feeds",
    "mdi:flag",
    { accent: "flag" },
  ),
  libraryBooks: createMenuItem(
    "library.books.title",
    "/library/books",
    "mdi:book-multiple",
    { accent: "library" },
  ),
};

const createGroup = (labelKey, items) => ({ labelKey, items });

const ROLE_CONFIGS = {
  student: [
    createGroup("sidebar.group.main", [
      BASE_MENU_ITEMS.dashboard,
      BASE_MENU_ITEMS.feeds,
      BASE_MENU_ITEMS.studentReports,
      BASE_MENU_ITEMS.lessonBooks,
    ]),
    createGroup("sidebar.group.certifications", [
      ...CERTIFICATE_MENU_BY_ROLE.student.map((i) =>
        createMenuItem(i.titleKey, i.path, i.icon)
      ),
    ]),
    createGroup("sidebar.group.classRoom", [
      { ...BASE_MENU_ITEMS.chats, titleKey: "sidebar.classRoom" },
      BASE_MENU_ITEMS.classes,
    ]),
    createGroup("sidebar.group.account", [BASE_MENU_ITEMS.personalNotes]),
  ],

  teacher: [
    createGroup("sidebar.group.main", [
      BASE_MENU_ITEMS.dashboard,
      BASE_MENU_ITEMS.feeds,
      BASE_MENU_ITEMS.studentReports,
      BASE_MENU_ITEMS.reportedFeeds,
      BASE_MENU_ITEMS.students,
    ]),
    createGroup("sidebar.group.certifications", [
      ...CERTIFICATE_MENU_BY_ROLE.teacher.map((i) =>
        createMenuItem(i.titleKey, i.path, i.icon)
      ),
    ]),
    createGroup("sidebar.group.classRoom", [
      BASE_MENU_ITEMS.chats,
      BASE_MENU_ITEMS.classes,
    ]),
    createGroup("sidebar.group.readingMaterial", [
      BASE_MENU_ITEMS.lessonBooks,
      BASE_MENU_ITEMS.trainingLesson,
    ]),
    createGroup("sidebar.group.account", [BASE_MENU_ITEMS.personalNotes]),
  ],

  client: [
    createGroup("sidebar.group.main", [
      BASE_MENU_ITEMS.dashboard,
      BASE_MENU_ITEMS.feeds,
      BASE_MENU_ITEMS.studentReports,
      BASE_MENU_ITEMS.students,
    ]),
    createGroup("sidebar.group.certifications", [
      ...CERTIFICATE_MENU_BY_ROLE.client.map((i) =>
        createMenuItem(i.titleKey, i.path, i.icon)
      ),
    ]),
    createGroup("sidebar.group.account", [
      BASE_MENU_ITEMS.personalNotes,
      BASE_MENU_ITEMS.toDoLists,
    ]),
  ],

  associate: [
    createGroup("sidebar.group.main", [
      BASE_MENU_ITEMS.dashboard,
      BASE_MENU_ITEMS.feeds,
      BASE_MENU_ITEMS.studentReports,
      BASE_MENU_ITEMS.reportedFeeds,
    ]),
    createGroup("sidebar.group.classRoom", [
      BASE_MENU_ITEMS.classRoom,
      BASE_MENU_ITEMS.allCalls,
      BASE_MENU_ITEMS.classes,
      BASE_MENU_ITEMS.classPeriods,
    ]),
    createGroup("sidebar.group.members", [
      BASE_MENU_ITEMS.students,
      BASE_MENU_ITEMS.teachers,
      BASE_MENU_ITEMS.guardians,
    ]),
    createGroup("sidebar.group.certifications", [
      ...CERTIFICATE_MENU_BY_ROLE.associate.map((i) =>
        createMenuItem(i.titleKey, i.path, i.icon)
      ),
    ]),
    createGroup("sidebar.group.library", [
      BASE_MENU_ITEMS.libraryBooks,
    ]),
    createGroup("sidebar.group.readingMaterial", [
      BASE_MENU_ITEMS.lessonBooks,
      BASE_MENU_ITEMS.trainingLesson,
    ]),
    createGroup("sidebar.group.account", [
      BASE_MENU_ITEMS.settings,
      BASE_MENU_ITEMS.personalNotes,
    ]),
  ],
};

function mapGroupItems(groups, mapItems) {
  return groups
    .map((group) => ({
      ...group,
      items: mapItems(group.items || []),
    }))
    .filter((group) => group.items.length > 0);
}

function stripStudentReportsNav(groups) {
  return mapGroupItems(groups, (items) =>
    items.filter((item) => item?.path !== "/student-reports"),
  );
}

/** Hifz affiliates: sidebar label student lessons instead of students reports. */
function applyStudentProgressNavTitles(groups, userInfo) {
  const title = isHifzUlQuranCapability(userInfo)
    ? "sidebar.studentLessons"
    : "sidebar.studentsReports";
  return mapGroupItems(groups, (items) =>
    items.map((item) =>
      item?.path === "/student-reports" ? { ...item, titleKey: title } : item,
    ),
  );
}

/** Hide Jamia/Hifz-only nav: Subjects, Classes (/classes), and /subjects route matches. */
function stripSubjectsNav(groups) {
  return mapGroupItems(groups, (items) =>
    items
      .filter((item) => item?.path !== "/subjects" && item?.path !== "/classes")
      .map((item) => {
        if (Array.isArray(item?.matches)) {
          return {
            ...item,
            matches: item.matches.filter((m) => m !== "/subjects"),
          };
        }
        return item;
      }),
  );
}

/** Class periods are Jamia / Hifz only — hide for online_school-only portals. */
function stripClassPeriodsNav(groups) {
  return mapGroupItems(groups, (items) =>
    items.filter((item) => item?.path !== "/class-periods"),
  );
}

/** Library is Jamia only. */
function stripLibraryNav(groups) {
  return groups
    .filter((group) => group?.labelKey !== "sidebar.group.library")
    .map((group) => ({
      ...group,
      items: (group.items || []).filter(
        (item) => !String(item?.path || "").startsWith("/library"),
      ),
    }))
    .filter((group) => group.items.length > 0);
}

/** Without online_school: show Gallery under Main instead of Reading Material group. */
function useGalleryInsteadOfReadingMaterialGroup(groups) {
  let galleryItem = null;
  const withoutReading = groups
    .map((group) => {
      if (group?.labelKey !== "sidebar.group.readingMaterial") return group;
      galleryItem =
        (group.items || []).find((item) => item?.path === "/lesson-books") ||
        BASE_MENU_ITEMS.lessonBooks;
      return null;
    })
    .filter(Boolean);

  if (!galleryItem) return withoutReading;

  return withoutReading.map((group) => {
    if (group?.labelKey !== "sidebar.group.main") return group;
    const hasGallery = group.items.some((item) => item?.path === "/lesson-books");
    if (hasGallery) return group;
    return { ...group, items: [...group.items, galleryItem] };
  });
}

function stripTrainingLessonNav(groups) {
  return mapGroupItems(groups, (items) =>
    items.filter((item) => item?.path !== "/training-lesson"),
  );
}

function stripClassCallsNav(groups) {
  return mapGroupItems(groups, (items) =>
    items.filter((item) => item?.path !== "/class-calls"),
  );
}

/** Teachers only: Students list requires the affiliate (associate) to have online_school. */
function stripStudentsNav(groups) {
  return mapGroupItems(groups, (items) =>
    items.filter((item) => item?.path !== "/students"),
  );
}

function stripCertificatesNav(groups) {
  return groups.filter((group) => group?.labelKey !== "sidebar.group.certifications");
}

function translateGroups(groups, t) {
  return groups.map((group) => ({
    label: group.labelKey ? t(group.labelKey) : group.label,
    labelKey: group.labelKey,
    items: (group.items || []).map((item) => ({
      ...item,
      title: item?.titleKey ? t(item.titleKey) : item?.title,
    })),
  }));
}

const sidebarConfig = (userInfo = {}, t = i18n.t.bind(i18n)) => {
  const user_type = userInfo.user_type || "associate";
  let config = (ROLE_CONFIGS[user_type] || ROLE_CONFIGS.associate).map((group) => ({
    ...group,
    items: [...(group.items || [])],
  }));

  if (!isStudentReportsCapability(userInfo) || usesHifzClassScopedStudentLessons(userInfo)) {
    config = stripStudentReportsNav(config);
  }
  if (!isOnlineSchoolCapability(userInfo)) {
    config = useGalleryInsteadOfReadingMaterialGroup(config);
    config = stripTrainingLessonNav(config);
    config = stripClassCallsNav(config);
  }
  if (!teacherHasGlobalStudentsListAccess(userInfo)) {
    config = stripStudentsNav(config);
  }
  if (!isJamiaOrHifzUlQuranCapability(userInfo)) {
    config = stripSubjectsNav(config);
    config = stripClassPeriodsNav(config);
  }
  if (!isJamiaCapability(userInfo)) {
    config = stripLibraryNav(config);
  }
  if (!isCertificatesCapability(userInfo)) {
    config = stripCertificatesNav(config);
  }
  config = applyStudentProgressNavTitles(config, userInfo);
  return translateGroups(config, t);
};

export default sidebarConfig;
